import { useNavigate } from 'react-router-dom';
import RecommendedPedalCard from './RecommendedPedalCard';
import { auth } from '../firebase';
import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useMyWants } from '../hooks/useMyWants';

/**
 * FirstMatchModal — shown exactly once, the first time a user's pedal
 * attracts a want-list match. Controlled entirely by the parent:
 *
 *   isOpen   — whether to render the modal
 *   pedals   — up to 3 enriched pedal objects (same shape as recommendedPedals
 *              in UserDashboard — matchInfo.wantedPedals populated)
 *   onClose  — called on X dismiss or after CTA navigation; parent is
 *              responsible for marking firstMatchModalShown = true in Firestore
 */
const FirstMatchModal = ({ isOpen, pedals = [], onClose }) => {
  const currentUser = auth.currentUser;
  const { firestoreUser } = useContext(UserContext);
  // One want-list listener shared by the modal's heart badges.
  const { wants: myWants } = useMyWants(currentUser);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const displayPedals = pedals.slice(0, 3);

  const handleCta = () => {
    onClose();
    navigate('/recommendations');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="People already want your pedal"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.52)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: 'Nunito, sans-serif'
      }}
    >
      <style>{`
        .fmm-sheet {
          background: #fff;
          border-radius: 25px;
          width: 100%;
          max-width: 1060px;
          max-height: calc(100dvh - 2rem);
          overflow-y: auto;
          overflow-x: hidden;
          box-shadow: 0 8px 40px rgba(0,0,0,0.18);
          display: flex;
          flex-direction: column;
        }
        .fmm-header {
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          padding: 18px 24px 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          flex-shrink: 0;
        }
        .fmm-title {
          margin: 0;
          font-family: Nunito, sans-serif;
          font-size: 1.45rem;
          font-weight: 800;
          color: #C89F3C;
          line-height: 1.2;
        }
        .fmm-sub {
          margin: 8px 0 0;
          font-family: Nunito, sans-serif;
          font-size: 1.03rem;
          font-weight: 600;
          color: #6B7280;
          line-height: 1.5;
          max-width: 700px;
        }
        .fmm-cards {
          padding: 20px 20px 0;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          flex-shrink: 0;
        }
        .fmm-cards-inner {
          display: flex;
          gap: 20px;
          width: max-content;
          padding-bottom: 4px;
          zoom: 0.85;
        }
        /* Cards are display-only — disable link navigation and pointer cursor */
        .fmm-sheet a {
          pointer-events: none;
          cursor: default;
        }
        /* Hide Active Trader badge inside this modal */
        .fmm-sheet .active-trader-badge {
          display: none !important;
        }
        .fmm-cta-wrap {
          padding: 16px 20px 20px;
          flex-shrink: 0;
        }
        /* Gold banner override — only inside this modal */
        .fmm-sheet .dashboard-rec-card-wants-text {
          background-color: rgba(200, 159, 60, 0.92) !important;
        }
        /* ── Mobile ── */
        @media (max-width: 768px) {
          .fmm-sheet {
            border-radius: 18px;
            max-height: calc(100dvh - 1.5rem);
          }
          .fmm-header {
            padding: 14px 16px 12px;
          }
          .fmm-title {
            font-size: 1.18rem;
          }
          .fmm-sub {
            font-size: 0.94rem;
            margin-top: 6px;
          }
          .fmm-cards {
            padding: 14px 12px 0;
          }
          .fmm-cards-inner {
            gap: 14px;
            /* Scale cards down so one-and-a-bit are visible, hinting at scroll */
            zoom: 0.78;
          }
          .fmm-cta-wrap {
            padding: 12px 16px 16px;
          }
        }
      `}</style>

      <div className="fmm-sheet">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="fmm-header">
          <div>
            <h2 className="fmm-title">People already want your pedal!</h2>
            <p className="fmm-sub">
              Visit Trade Opportunities to explore an instant trade.
            </p>
          </div>

          {/* Dismiss X */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '22px',
              lineHeight: 1,
              color: '#9CA3AF',
              padding: '2px 4px',
              flexShrink: 0,
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            ×
          </button>
        </div>

        {/* ── Cards ──────────────────────────────────────────────────── */}
        <div className="fmm-cards">
          <div className="fmm-cards-inner">
            {displayPedals.map((pedal) => (
              <div key={pedal.id} style={{ flexShrink: 0 }}>
                <RecommendedPedalCard pedal={pedal} currentUser={currentUser} firestoreUser={firestoreUser} myWants={myWants} />
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ────────────────────────────────────────────────────── */}
        <div className="fmm-cta-wrap">
          <button
            type="button"
            onClick={handleCta}
            style={{
              display: 'block',
              width: '100%',
              padding: '14px',
              borderRadius: '9999px',
              border: 'none',
              backgroundColor: '#5A9ECB',
              color: '#ffffff',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1.05rem',
              fontWeight: 800,
              cursor: 'pointer',
              letterSpacing: '0.01em'
            }}
          >
            See Who Wants to Trade
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstMatchModal;
